import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../api/client';

function scoreColor(ratio: number): string {
  if (ratio >= 1.0) return 'var(--state-positive)';
  if (ratio >= 0.75) return 'var(--text-primary)';
  return 'var(--state-warning)';
}

function issuePill(count: number): string {
  if (count === 0) return 'pill pill-sage';
  if (count <= 5) return 'pill pill-teal';
  return 'pill pill-warning';
}

interface SnapshotTerritory {
  name: string;
  average: number;
  totalIssues: number;
  consultantCount: number;
  teams: {
    teamId: string;
    teamName: string;
    average: number;
    totalIssues: number;
    members: { userId: string; name: string; overall: number; totalIssues: number }[];
  }[];
}

interface SnapshotData {
  companyAverage: number;
  companyIssues: number;
  consultantCount: number;
  territories: SnapshotTerritory[];
  generatedAt?: string;
}

export function OrgSnapshot() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState<SnapshotData | null>(null);
  const [expandedTerr, setExpandedTerr] = useState<Set<string>>(new Set());
  const [expandedTeam, setExpandedTeam] = useState<Set<string>>(new Set());

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true);
    try {
      const result = await api.getOrgSnapshot(refresh);
      setData(result);
    } catch { /* ignore */ }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await api.getOrgSnapshot();
        if (!cancelled) setData(result);
      } catch { /* ignore */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleTerr = (name: string) => {
    setExpandedTerr(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const toggleTeam = (id: string) => {
    setExpandedTeam(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const rowStyle = (depth: number): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    padding: `8px 12px 8px ${12 + depth * 20}px`,
    borderBottom: '1px solid var(--border-primary)',
    cursor: 'pointer',
    fontSize: '13px',
  });

  if (loading) {
    return (
      <div className="card" style={{ marginBottom: '24px', textAlign: 'center', padding: '20px' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Loading company snapshot…</span>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="card" style={{ marginBottom: '24px' }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Company Snapshot</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', textTransform: 'none', letterSpacing: 0 }}>
          {data.generatedAt && (
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              Updated {new Date(data.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            className="btn btn-secondary"
            style={{ padding: '2px 10px', fontSize: '12px' }}
            disabled={refreshing}
            onClick={() => load(true)}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </span>
      </div>

      {/* Company average summary */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', margin: '8px 0 16px', padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Company Average</div>
          <div style={{ fontSize: '24px', fontWeight: 600, color: scoreColor(data.companyAverage) }}>
            {Math.round(data.companyAverage * 100)}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Issues</div>
          <div style={{ fontSize: '24px', fontWeight: 600 }}>{data.companyIssues}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Consultants</div>
          <div style={{ fontSize: '24px', fontWeight: 600 }}>{data.consultantCount}</div>
        </div>
      </div>

      {/* Territory breakdown */}
      <div style={{ border: '1px solid var(--border-primary)', borderRadius: '6px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', padding: '8px 12px', background: 'var(--bg-secondary)', fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border-primary)' }}>
          <span style={{ flex: 1 }}>Name</span>
          <span style={{ width: '80px', textAlign: 'right' }}>Score</span>
          <span style={{ width: '80px', textAlign: 'right' }}>Issues</span>
        </div>

        {data.territories.map(terr => (
          <React.Fragment key={terr.name}>
            <div style={rowStyle(0)} onClick={() => toggleTerr(terr.name)}>
              <span style={{ flex: 1, fontWeight: 600 }}>
                {expandedTerr.has(terr.name) ? '▾' : '▸'} {terr.name}
                <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 400, color: 'var(--text-secondary)' }}>
                  {terr.consultantCount} consultant{terr.consultantCount !== 1 ? 's' : ''}
                </span>
              </span>
              <span style={{ width: '80px', textAlign: 'right', fontWeight: 600, color: scoreColor(terr.average) }}>
                {Math.round(terr.average * 100)}%
              </span>
              <span style={{ width: '80px', textAlign: 'right' }}>
                <span className={issuePill(terr.totalIssues)}>{terr.totalIssues}</span>
              </span>
            </div>

            {expandedTerr.has(terr.name) && terr.teams.map(team => (
              <React.Fragment key={team.teamId}>
                <div style={rowStyle(1)} onClick={() => toggleTeam(team.teamId)}>
                  <span style={{ flex: 1, fontWeight: 500 }}>
                    {expandedTeam.has(team.teamId) ? '▾' : '▸'} {team.teamName}
                  </span>
                  <span style={{ width: '80px', textAlign: 'right', fontWeight: 500, color: scoreColor(team.average) }}>
                    {Math.round(team.average * 100)}%
                  </span>
                  <span style={{ width: '80px', textAlign: 'right' }}>
                    <span className={issuePill(team.totalIssues)}>{team.totalIssues}</span>
                  </span>
                </div>

                {expandedTeam.has(team.teamId) && team.members.map(m => (
                  <div
                    key={m.userId}
                    style={{ ...rowStyle(2), cursor: 'pointer' }}
                    onClick={() => navigate(`/?userId=${m.userId}`)}
                  >
                    <span style={{ flex: 1 }}>{m.name}</span>
                    <span style={{ width: '80px', textAlign: 'right', color: scoreColor(m.overall) }}>
                      {Math.round(m.overall * 100)}%
                    </span>
                    <span style={{ width: '80px', textAlign: 'right' }}>
                      <span className={issuePill(m.totalIssues)}>{m.totalIssues}</span>
                    </span>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
